import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInteractionTemplate, createInteractionTemplate, updateInteractionTemplate, renderTemplate, getIndustries, getIndustryGroups } from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box, Typography, Button, TextField, Paper, Alert, Grid, Chip,
  CircularProgress, MenuItem, FormControlLabel, Checkbox, Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';

const CHANNELS = [
  { value: 'Email', label: 'Email' },
  { value: 'LinkedIn', label: 'LinkedIn' },
];

const STAGES = [
  { value: '', label: '— Any Stage —' },
  { value: 'FirstContact', label: 'First Contact' },
  { value: 'FollowUp', label: 'Follow-Up' },
  { value: 'Qualification', label: 'Qualification' },
  { value: 'ReEngagement', label: 'Re-Engagement' },
  { value: 'DemoInvite', label: 'Demo Invite' },
  { value: 'Negotiation', label: 'Negotiation' },
  { value: 'Closed', label: 'Closed' },
];

const INTERACTION_TYPES = [
  { value: 'Email', label: 'Email' },
  { value: 'PhoneCall', label: 'Phone Call' },
  { value: 'Meeting', label: 'Meeting' },
  { value: 'VideoCall', label: 'Video Call' },
  { value: 'Other', label: 'Other' },
];

const PLACEHOLDER_HINTS = [
  '{{CompanyName}}', '{{ContactFirstName}}', '{{ContactLastName}}', '{{ContactJobTitle}}',
  '{{Industry}}', '{{Website}}', '{{SenderName}}', '{{SenderTitle}}',
];

// Quill destroys tables, doctype, body wrappers, inline styles, etc.
// If template contains any of those, the visual editor is unsafe — lock to HTML mode.
const RICH_HTML_RE = /<!doctype|<html[\s>]|<body[\s>]|<table[\s>]|<tr[\s>]|<td[\s>]|<th[\s>]|<style[\s>]|style\s*=\s*["'][^"']*(?:background|border-radius|box-shadow|padding|margin|width|height|font-family)/i;
const containsRichHtml = (s) => !!s && RICH_HTML_RE.test(s);

const PERSONAS = [
  { value: '', label: '— Any —' },
  { value: 'CTO', label: 'CTO' },
  { value: 'CFO', label: 'CFO' },
  { value: 'CEO', label: 'CEO' },
  { value: 'ProductManager', label: 'Product Manager' },
  { value: 'VP_Engineering', label: 'VP Engineering' },
  { value: 'VP_Sales', label: 'VP Sales' },
  { value: 'Other', label: 'Other' },
];

const COMPANY_SIZES = [
  { value: '', label: '— Any —' },
  { value: 'SMB', label: 'SMB (< 100)' },
  { value: 'Mid-market', label: 'Mid-market (100-1000)' },
  { value: 'Enterprise', label: 'Enterprise (1000+)' },
];

const TONES = [
  { value: '', label: '— Any —' },
  { value: 'Formal', label: 'Formal' },
  { value: 'Direct', label: 'Direct' },
  { value: 'Friendly', label: 'Friendly' },
  { value: 'Empathetic', label: 'Empathetic' },
  { value: 'Concise', label: 'Concise' },
];

const GOALS = [
  { value: '', label: '— Any —' },
  { value: 'Awareness', label: 'Awareness' },
  { value: 'Discovery', label: 'Discovery' },
  { value: 'Demo', label: 'Demo' },
  { value: 'Negotiation', label: 'Negotiation' },
  { value: 'Closing', label: 'Closing' },
  { value: 'NurtureRevive', label: 'Nurture / Revive' },
];

const CHAIN_STEPS = [
  { value: 'PersonalizeTemplate', label: 'Personalize for lead' },
  { value: 'ImproveOutreach', label: 'Improve copywriting' },
  { value: 'TranslateOutreach', label: 'Translate' },
  { value: 'Shorten', label: 'Shorten' },
  { value: 'Expand', label: 'Expand' },
];

export default function InteractionTemplatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [industries, setIndustries] = useState([]);
  const [industryGroupOptions, setIndustryGroupOptions] = useState([]);
  const [industryGroupIds, setIndustryGroupIds] = useState([]);

  // Form state
  const [name, setName] = useState('');
  const [channel, setChannel] = useState('Email');
  const [stage, setStage] = useState('');
  const [interactionType, setInteractionType] = useState('Email');
  const [subjectTemplate, setSubjectTemplate] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [language, setLanguage] = useState('en');
  const [industryTags, setIndustryTags] = useState('');
  const [persona, setPersona] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [tone, setTone] = useState('');
  const [maxLength, setMaxLength] = useState('');
  const [goal, setGoal] = useState('');
  const [enrichmentChain, setEnrichmentChain] = useState([]);
  const [openings, setOpenings] = useState([]); // [{tone, text}]
  const [newOpeningTone, setNewOpeningTone] = useState('Friendly');
  const [newOpeningText, setNewOpeningText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [htmlMode, setHtmlMode] = useState(true);

  // Preview state
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Version info
  const [version, setVersion] = useState(1);
  const [parentTemplateId, setParentTemplateId] = useState(null);

  const loadTemplate = useCallback(async () => {
    try {
      const { data } = await getInteractionTemplate(id);
      setName(data.name || '');
      setChannel(data.channel || 'Email');
      setStage(data.stage || '');
      setInteractionType(data.interactionType || 'Email');
      setSubjectTemplate(data.subjectTemplate || '');
      setBodyTemplate(data.bodyTemplate || '');
      if (containsRichHtml(data.bodyTemplate)) setHtmlMode(true);
      setLanguage(data.language || 'en');
      // industryTags is stored as JSON string in DB; could come back as JSON array string,
      // raw array (legacy), or plain string. Normalize to comma display.
      const rawTags = data.industryTags;
      let displayTags = '';
      if (Array.isArray(rawTags)) {
        displayTags = rawTags.join(', ');
      } else if (typeof rawTags === 'string' && rawTags.trim().startsWith('[')) {
        try {
          const arr = JSON.parse(rawTags);
          displayTags = Array.isArray(arr) ? arr.join(', ') : rawTags;
        } catch {
          displayTags = rawTags;
        }
      } else {
        displayTags = rawTags || '';
      }
      setIndustryTags(displayTags);
      // industryGroupIds: jsonb array of ints, may arrive as string or array
      const rawGroupIds = data.industryGroupIds;
      let groupIdArr = [];
      if (Array.isArray(rawGroupIds)) {
        groupIdArr = rawGroupIds.filter((x) => Number.isFinite(x));
      } else if (typeof rawGroupIds === 'string' && rawGroupIds.trim().startsWith('[')) {
        try {
          const arr = JSON.parse(rawGroupIds);
          if (Array.isArray(arr)) groupIdArr = arr.filter((x) => Number.isFinite(x));
        } catch { /* ignore */ }
      }
      setIndustryGroupIds(groupIdArr);
      setPersona(data.persona || '');
      setCompanySize(data.companySize || '');
      setTone(data.tone || '');
      setMaxLength(data.maxLength != null ? String(data.maxLength) : '');
      setGoal(data.goal || '');
      try {
        const chain = data.enrichmentChain ? JSON.parse(data.enrichmentChain) : [];
        setEnrichmentChain(Array.isArray(chain) ? chain : []);
      } catch { setEnrichmentChain([]); }
      try {
        const ops = data.openings ? JSON.parse(data.openings) : [];
        setOpenings(Array.isArray(ops) ? ops : []);
      } catch { setOpenings([]); }
      setIsActive(data.isActive !== false);
      setVersion(data.version || 1);
      setParentTemplateId(data.parentTemplateId || null);
    } catch {
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) loadTemplate();
  }, [isNew, loadTemplate]);

  useEffect(() => {
    getIndustries().then(r => setIndustries(r.data)).catch(() => {});
    getIndustryGroups().then(r => setIndustryGroupOptions(r.data || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!bodyTemplate.trim()) { setError('Body template is required'); return; }
    setError('');
    setSaving(true);
    try {
      const tagArr = industryTags
        ? industryTags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      const payload = {
        name: name.trim(),
        channel,
        stage: stage || null,
        interactionType,
        subjectTemplate: subjectTemplate.trim(),
        bodyTemplate,
        language: language.trim() || 'en',
        industryTags: tagArr.length > 0 ? JSON.stringify(tagArr) : null,
        industryGroupIds: industryGroupIds.length > 0 ? JSON.stringify(industryGroupIds) : null,
        persona: persona || null,
        companySize: companySize || null,
        tone: tone || null,
        maxLength: maxLength !== '' && !Number.isNaN(Number(maxLength)) ? Number(maxLength) : null,
        goal: goal || null,
        enrichmentChain: enrichmentChain.length > 0 ? JSON.stringify(enrichmentChain) : null,
        openings: openings.length > 0 ? JSON.stringify(openings) : null,
        isActive,
      };
      if (isNew) {
        await createInteractionTemplate(payload);
      } else {
        await updateInteractionTemplate(id, payload);
      }
      navigate('/management?tab=interaction-templates');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError('');
    try {
      const { data } = await renderTemplate({
        templateId: isNew ? null : parseInt(id),
        subjectTemplate: subjectTemplate,
        bodyTemplate: bodyTemplate,
        placeholders: {
          CompanyName: 'Acme Corp',
          ContactFirstName: 'John',
          ContactLastName: 'Doe',
          ContactJobTitle: 'CTO',
          Industry: 'Technology',
          Website: 'www.acme.com',
          SenderName: 'Jane Smith',
          SenderTitle: 'Sales Manager',
        }
      });
      setPreviewSubject(data.renderedSubject || '');
      setPreviewBody(data.renderedBody || '');
      setShowPreview(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    if (htmlMode) {
      setBodyTemplate(prev => prev + placeholder);
    } else {
      setBodyTemplate(prev => prev + placeholder);
    }
  };

  if (loading) return (
    <Box sx={{ p: 5, textAlign: 'center' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/management?tab=interaction-templates')}>
          Back to Templates
        </Button>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        {isNew ? 'New Interaction Template' : 'Edit Template'}
      </Typography>
      {!isNew && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Version {version}{parentTemplateId ? ' (derived)' : ''}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Editor */}
        <Grid size={{ xs: 12, md: showPreview ? 6 : 12 }}>
          <Paper sx={{ p: 3 }}>
            <TextField
              label="Template Name *"
              size="small"
              fullWidth
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Outreach - SaaS"
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Channel"
                  size="small"
                  fullWidth
                  value={channel}
                  onChange={e => setChannel(e.target.value)}
                >
                  {CHANNELS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Stage"
                  size="small"
                  fullWidth
                  value={stage}
                  onChange={e => setStage(e.target.value)}
                >
                  {STAGES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Interaction Type"
                  size="small"
                  fullWidth
                  value={interactionType}
                  onChange={e => setInteractionType(e.target.value)}
                >
                  {INTERACTION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Language"
                  size="small"
                  fullWidth
                  value={language}
                  onChange={e => setLanguage(e.target.value)}
                  placeholder="en"
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <TextField
                label="Industry Tags"
                size="small"
                fullWidth
                value={industryTags}
                onChange={e => setIndustryTags(e.target.value)}
                placeholder="Comma-separated, e.g. Technology, Finance"
              />
              {industries.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {industries.slice(0, 10).map(ind => (
                    <Chip
                      key={ind.id}
                      size="small"
                      clickable
                      label={ind.name}
                      onClick={() => {
                        const tags = industryTags ? industryTags.split(',').map(t => t.trim()) : [];
                        if (!tags.includes(ind.name)) setIndustryTags([...tags, ind.name].join(', '));
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Autocomplete
                multiple
                size="small"
                options={industryGroupOptions}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(o, v) => o.id === (typeof v === 'object' ? v?.id : v)}
                value={industryGroupOptions.filter((g) => industryGroupIds.includes(g.id))}
                onChange={(_, v) => setIndustryGroupIds((v || []).map((g) => g.id))}
                renderInput={(params) => (
                  <TextField {...params} label="Industry Groups" placeholder="Select groups this template targets" />
                )}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Templates target industry groups. The email renderer still substitutes the lead&apos;s exact industry name.
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Persona" size="small" fullWidth
                  value={persona} onChange={e => setPersona(e.target.value)}>
                  {PERSONAS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Company Size" size="small" fullWidth
                  value={companySize} onChange={e => setCompanySize(e.target.value)}>
                  {COMPANY_SIZES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Tone" size="small" fullWidth
                  value={tone} onChange={e => setTone(e.target.value)}>
                  {TONES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Goal" size="small" fullWidth
                  value={goal} onChange={e => setGoal(e.target.value)}>
                  {GOALS.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  type="number"
                  label="Max length (chars)"
                  size="small"
                  fullWidth
                  value={maxLength}
                  onChange={e => setMaxLength(e.target.value)}
                  inputProps={{ min: 0, max: 8000, step: 10 }}
                  helperText={channel === 'LinkedIn'
                    ? 'LinkedIn: 200 (connect request) / 8000 (DM). Auto-applied in LinkedIn assist.'
                    : 'Optional cap on rendered/generated message length. Leave blank for no limit.'}
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Opening lines (per tone)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Add 1-5 opening sentences. On Compose Email / LinkedIn, sender picks tone → opening prepends to body.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                <TextField
                  select size="small" label="Tone" value={newOpeningTone}
                  onChange={e => setNewOpeningTone(e.target.value)}
                  sx={{ minWidth: 130 }}
                >
                  {TONES.filter(t => t.value).map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
                <TextField
                  size="small" fullWidth multiline maxRows={3}
                  label="Opening text" value={newOpeningText}
                  onChange={e => setNewOpeningText(e.target.value)}
                  placeholder="e.g. Hope your week's been smooth — wanted to share a quick observation about {{Industry}} teams like {{CompanyName}}."
                />
                <Button
                  variant="outlined" size="small"
                  disabled={!newOpeningText.trim()}
                  onClick={() => {
                    setOpenings([...openings, { tone: newOpeningTone, text: newOpeningText.trim() }]);
                    setNewOpeningText('');
                  }}
                >Add</Button>
              </Box>
              {openings.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {openings.map((o, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                      <Chip size="small" label={o.tone} sx={{ flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>{o.text}</Typography>
                      <Button size="small" color="error"
                        onClick={() => setOpenings(openings.filter((_, idx) => idx !== i))}>×</Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Auto-Pipeline (Enrichment Chain)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Ordered AI steps applied when "Run Auto-Pipeline" is clicked on Compose. Click chips to add. Drag-style reorder not supported — clear and re-add to change order.
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {CHAIN_STEPS.map(s => (
                  <Chip
                    key={s.value}
                    variant="outlined"
                    size="small"
                    clickable
                    label={`+ ${s.label}`}
                    onClick={() => setEnrichmentChain([...enrichmentChain, s.value])}
                  />
                ))}
                {enrichmentChain.length > 0 && (
                  <Chip variant="outlined" size="small" color="error" clickable
                    label="Clear chain" onClick={() => setEnrichmentChain([])} />
                )}
              </Box>
              {enrichmentChain.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {enrichmentChain.map((step, i) => (
                    <React.Fragment key={i}>
                      <Chip
                        size="small"
                        label={`${i + 1}. ${step}`}
                        onDelete={() => setEnrichmentChain(enrichmentChain.filter((_, idx) => idx !== i))}
                      />
                      {i < enrichmentChain.length - 1 && <Typography sx={{ mx: 0.25 }}>→</Typography>}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>

            {channel === 'Email' && (
              <TextField
                label="Subject Template"
                size="small"
                fullWidth
                value={subjectTemplate}
                onChange={e => setSubjectTemplate(e.target.value)}
                placeholder="e.g. {{ContactFirstName}}, quick question about {{CompanyName}}"
                sx={{ mb: 2 }}
              />
            )}

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Body Template *</Typography>
                <Chip
                  variant="outlined"
                  size="small"
                  clickable
                  label={htmlMode ? '← Visual Editor' : 'HTML Code →'}
                  onClick={() => {
                    if (htmlMode && containsRichHtml(bodyTemplate)) {
                      setError('Visual editor would strip tables/inline styles. Stay in HTML mode or remove rich markup first.');
                      return;
                    }
                    setError('');
                    setHtmlMode(!htmlMode);
                  }}
                />
              </Box>

              {/* Placeholder buttons */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {PLACEHOLDER_HINTS.map(ph => (
                  <Chip
                    key={ph}
                    variant="outlined"
                    size="small"
                    clickable
                    label={ph}
                    onClick={() => insertPlaceholder(ph)}
                    sx={{ fontFamily: 'monospace' }}
                  />
                ))}
              </Box>

              {htmlMode ? (
                <TextField
                  multiline
                  rows={14}
                  fullWidth
                  value={bodyTemplate}
                  onChange={e => setBodyTemplate(e.target.value)}
                  placeholder="Enter HTML template content..."
                  slotProps={{
                    input: {
                      sx: { fontFamily: 'monospace', fontSize: '0.85rem' }
                    }
                  }}
                />
              ) : (
                <ReactQuill
                  theme="snow"
                  value={bodyTemplate}
                  onChange={setBodyTemplate}
                  style={{ minHeight: 200 }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ 'color': [] }],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                />
              )}
            </Box>

            <FormControlLabel
              control={<Checkbox checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
              label="Active"
              sx={{ mb: 2 }}
            />

            <Paper
              elevation={3}
              sx={{
                position: 'sticky',
                bottom: 0,
                p: 2,
                mt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button variant="outlined" onClick={() => navigate('/management?tab=interaction-templates')}>
                Cancel
              </Button>
              <Button variant="outlined" onClick={handlePreview} disabled={previewing}>
                {previewing ? 'Rendering...' : '👁 Preview'}
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : isNew ? 'Create Template' : 'Save Changes'}
              </Button>
            </Paper>
          </Paper>
        </Grid>

        {/* Preview panel */}
        {showPreview && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Preview (Sample Data)</Typography>
                <Button variant="outlined" size="small" startIcon={<CloseIcon />} onClick={() => setShowPreview(false)}>
                  Close
                </Button>
              </Box>
              {previewSubject && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Subject</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{previewSubject}</Typography>
                </Box>
              )}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <div dangerouslySetInnerHTML={{ __html: previewBody }} />
              </Paper>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
